import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AboutVideosManager = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({ youtube_url: '', title: '' });
  const [saving, setSaving] = useState(false);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/cms/about-videos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setVideos(response.data);
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^#&?]*)/,
      /youtube\.com\/live\/([^#&?]*)/,
      /youtube\.com\/shorts\/([^#&?]*)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1] && match[1].length >= 11) {
        return match[1].substring(0, 11);
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.youtube_url) {
      alert('Por favor, introduce una URL de YouTube');
      return;
    }

    const videoId = getYouTubeId(formData.youtube_url);
    if (!videoId) {
      alert('URL de YouTube no válida');
      return;
    }

    setSaving(true);
    try {
      if (editingVideo) {
        await axios.put(`${API}/cms/about-videos/${editingVideo.id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        alert('Video actualizado correctamente');
      } else {
        await axios.post(`${API}/cms/about-videos`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        alert('Video añadido correctamente');
      }
      setShowAddModal(false);
      setEditingVideo(null);
      setFormData({ youtube_url: '', title: '' });
      loadVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Error al guardar el video');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`¿Eliminar el video "${video.title || 'Sin título'}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/cms/about-videos/${video.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      alert('Video eliminado correctamente');
      loadVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Error al eliminar el video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({ youtube_url: video.youtube_url, title: video.title || '' });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingVideo(null);
    setFormData({ youtube_url: '', title: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Videos - Sobre Nosotros</h1>
          <p className="text-gray-600">Gestiona los videos que aparecen en la sección Sobre Nosotros</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Añadir Video
        </button>
      </div>

      {/* Video Grid */}
      {videos.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-6xl mb-4">🎬</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay videos</h3>
          <p className="text-gray-500 mb-4">Añade videos de YouTube para mostrar cómo trabajáis</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            Añadir Primer Video
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => {
            const videoId = getYouTubeId(video.youtube_url);
            return (
              <div key={video.id} className="bg-white rounded-lg shadow overflow-hidden">
                {videoId ? (
                  <div className="aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title={video.title || 'Video'}
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-200 flex items-center justify-center">
                    <p className="text-gray-500">Video no disponible</p>
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 mb-2">{video.title || 'Sin título'}</h3>
                  <p className="text-sm text-gray-500 truncate mb-3">{video.youtube_url}</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(video)}
                      className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(video)}
                      className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingVideo ? 'Editar Video' : 'Añadir Video'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de YouTube *
                </label>
                <input
                  type="text"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Soporta: youtube.com/watch, youtu.be, youtube.com/live, youtube.com/shorts
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título (opcional)
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ej: Cómo reformamos una vivienda"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Preview */}
              {formData.youtube_url && getYouTubeId(formData.youtube_url) && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vista previa
                  </label>
                  <div className="aspect-video rounded-md overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeId(formData.youtube_url)}`}
                      title="Preview"
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : (editingVideo ? 'Actualizar' : 'Añadir')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">💡 Consejos para los Videos</h3>
        <ul className="text-sm text-blue-800 space-y-2">
          <li>• <strong>Calidad:</strong> Sube videos en alta calidad para una mejor experiencia</li>
          <li>• <strong>Duración:</strong> Videos cortos (1-3 minutos) funcionan mejor</li>
          <li>• <strong>Contenido:</strong> Muestra el proceso de reforma, antes/después, equipo de trabajo</li>
          <li>• <strong>Cantidad:</strong> 1-3 videos es ideal, más puede ser abrumador</li>
        </ul>
      </div>
    </div>
  );
};

export default AboutVideosManager;
